/** ESAT workspace scoring: per-module raw → % → grade band, shown as x.x/9.0 */

import {
  clearPaperScores,
  loadPaperScores,
  readUserCache,
  savePaperScore,
  writeUserCache,
} from './paperProgressCache';

export const ESAT_SCORES_KEY = 'esat_paper_scores';
export const ESAT_TARGET_KEY = 'esat_workspace_target';
export const ESAT_CHOSEN_OPTIONALS_KEY = 'esat_sitting_chosen_optionals';

/**
 * Percent correct → integer grade band (monotonic).
 * ≥90→7, 80–89→6, 70–79→5, 60–69→4, 50–59→3, 40–49→2, <40→1
 */
export function percentToEsatGrade(pct) {
  const p = Number(pct);
  if (!Number.isFinite(p)) return null;
  if (p >= 90) return 7;
  if (p >= 80) return 6;
  if (p >= 70) return 5;
  if (p >= 60) return 4;
  if (p >= 50) return 3;
  if (p >= 40) return 2;
  return 1;
}

export function gradeFromCorrectTotal(correct, total) {
  const t = Number(total);
  if (!t || t <= 0) return null;
  const c = Math.max(0, Number(correct) || 0);
  return percentToEsatGrade((c / t) * 100);
}

export function formatEsatGrade(grade) {
  if (grade == null || !Number.isFinite(Number(grade))) return '—';
  return `${Number(grade).toFixed(1)}/9.0`;
}

export function formatPaperRaw(correct, total) {
  if (total == null || total <= 0 || correct == null) return '—';
  return `${correct}/${total}`;
}

export function loadEsatPaperScores(userId) {
  return loadPaperScores(ESAT_SCORES_KEY, userId);
}

export function saveEsatPaperScore(userId, paperId, score = {}) {
  savePaperScore(ESAT_SCORES_KEY, userId, paperId, score);
}

export function clearEsatPaperScores(userId, paperIds = []) {
  clearPaperScores(ESAT_SCORES_KEY, userId, paperIds);
}

/** The user's sittingStartId -> { keys, updatedAt } map of chosen optional modules. */
export function loadAllEsatChosenOptionalKeys(userId) {
  return readUserCache(ESAT_CHOSEN_OPTIONALS_KEY, userId, {});
}

export function loadEsatChosenOptionalKeys(userId, sittingStartId) {
  if (!sittingStartId) return [];
  const all = loadAllEsatChosenOptionalKeys(userId);
  const entry = all[String(sittingStartId)];
  const keys = entry?.keys;
  return Array.isArray(keys) ? keys.filter(Boolean).map(String) : [];
}

export function saveEsatChosenOptionalKeys(userId, sittingStartId, keys) {
  if (!sittingStartId) return;
  const unique = [...new Set((keys || []).filter(Boolean).map(String))];
  if (!unique.length) return;
  const all = loadAllEsatChosenOptionalKeys(userId);
  all[String(sittingStartId)] = { keys: unique, updatedAt: Date.now() };
  writeUserCache(ESAT_CHOSEN_OPTIONALS_KEY, userId, all);
}

/**
 * Forget the user's cached scores for a sitting's module papers and the
 * optional modules chosen for it (a retake).
 */
export function resetEsatSittingProgress(userId, paperIds = [], sittingStartId = null) {
  const ids = (paperIds || []).filter(Boolean).map(String);
  clearEsatPaperScores(userId, ids);
  const all = loadAllEsatChosenOptionalKeys(userId);
  const remove = new Set(sittingStartId ? [...ids, String(sittingStartId)] : ids);
  let changed = false;
  Object.keys(all).forEach((key) => {
    if (remove.has(String(key))) {
      delete all[key];
      changed = true;
    }
  });
  if (changed) writeUserCache(ESAT_CHOSEN_OPTIONALS_KEY, userId, all);
}

/**
 * ESAT is reported PER MODULE, not as one combined number — the sitting-level
 * figure is the mean of the module grades actually sat.
 * moduleIds: ordered array of { key, id } — mathematics_1 first, then optionals.
 */
export function sittingScoreFromEsatModules(moduleIds, scoresMap = {}) {
  const modules = {};
  const grades = [];
  let mode = null;
  let anyPresent = false;
  let anyGraded = false;

  (moduleIds || []).forEach(({ key, id }) => {
    if (!id) return;
    anyPresent = true;
    const s = scoresMap[String(id)];
    const done = Boolean(s && s.total > 0);
    if (!done) return;
    anyGraded = true;
    const grade = gradeFromCorrectTotal(s.correct || 0, s.total || 0);
    modules[key] = { correct: s.correct || 0, total: s.total || 0, grade };
    if (grade != null) grades.push(grade);
    mode = mode || s.mode || null;
  });

  const gradedCount = Object.keys(modules).length;
  const presentCount = (moduleIds || []).filter((m) => m?.id).length;

  let status = 'none';
  if (anyPresent && anyGraded) {
    status = gradedCount >= presentCount ? 'done' : 'progress';
  }

  const grade = grades.length
    ? Math.round((grades.reduce((a, b) => a + b, 0) / grades.length) * 10) / 10
    : null;

  return { modules, grade, mode, status };
}

/**
 * N-paper version of TMUA's sittingStatusFromDbPapers (which is hardcoded to two papers).
 * all 'completed' -> 'done'; any 'in_progress'|'completed' -> 'progress'; else 'none'
 */
export function esatStatusFromDbPapers(paperIds = [], dbPapers = {}) {
  const statuses = paperIds
    .filter(Boolean)
    .map((id) => dbPapers[String(id)]?.status)
    .filter(Boolean);
  if (!statuses.length) return 'none';
  if (statuses.every((s) => s === 'completed')) return 'done';
  if (statuses.some((s) => s === 'in_progress' || s === 'completed')) return 'progress';
  return 'none';
}

export function loadEsatTarget(userId) {
  const fallback = {
    value: 6.0,
    label: 'University College London (UCL) — Electronic & Electrical Eng',
    custom: false,
  };
  return readUserCache(ESAT_TARGET_KEY, userId, fallback);
}

export function saveEsatTarget(userId, target) {
  writeUserCache(ESAT_TARGET_KEY, userId, target);
}
